// src/components/gestionEvento/RegistroFrom/PruebasFieldArray.js

import React, { useState } from 'react';
import { FieldArray } from 'formik';
import {
  Grid,
  TextField,
  IconButton,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Snackbar,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import PropTypes from 'prop-types';

const PruebasFieldArray = ({
  values,
  errors,
  touched,
  setFieldValue,
  handleChange,
  pruebasFiltradas,
  maxPruebas,
  maxRelevos,
  habilitarMarcasSiembra,
  opcionesRelevos,
}) => {
  const individualOptions = pruebasFiltradas.filter(
    (p) => p.testType.toLowerCase() !== 'relevos'
  );
  const relayOptions = pruebasFiltradas.filter(
    (p) => p.testType.toLowerCase() === 'relevos'
  );

  const getOptions = (dataArray, currentIndex, options) => {
    const alreadySelected = dataArray
      .filter((_, i) => i !== currentIndex)
      .map((p) => p.prueba);
    return options.filter((p) => !alreadySelected.includes(p.nombre));
  };

  const renderNumericField = (fieldName, label, value, arrayName, index) => {
    const maxAllowed = fieldName === 'marcaCentesimas' ? 99 : 59;
    return (
      <Grid item xs={6} sm={2} key={fieldName}>
        <TextField
          label={label}
          name={`${arrayName}[${index}].${fieldName}`}
          value={value || ''}
          onChange={handleChange}
          fullWidth
          size="small"
          inputProps={{
            maxLength: 2,
            onInput: (e) => {
              let inputValue = e.target.value;
              if (inputValue !== '' && !isNaN(inputValue)) {
                const numericValue = parseInt(inputValue, 10);
                if (numericValue > maxAllowed) {
                  inputValue = String(maxAllowed);
                  e.target.value = inputValue;
                  setFieldValue(`${arrayName}[${index}].${fieldName}`, inputValue.padStart(2, '0'));
                }
              }
              if (e.target.value.length >= 2) {
                const inputs = Array.from(document.querySelectorAll('input'));
                const currentIndex = inputs.indexOf(e.target);
                if (currentIndex > -1 && currentIndex < inputs.length - 1) {
                  inputs[currentIndex + 1].focus();
                }
              }
            },
            onBlur: (e) => {
              let val = e.target.value;
              if (val && val.length === 1) {
                const padded = val.padStart(2, '0');
                setFieldValue(`${arrayName}[${index}].${fieldName}`, padded);
              }
            },
          }}
        />
      </Grid>
    );
  };

  const renderMarcaFields = (item, index, arrayName) => {
    const tipoMarca = item.tipoMarca;
    if (!habilitarMarcasSiembra || !tipoMarca) return null;
    switch (tipoMarca) {
      case 'velocidad':
        return (
          <>
            {renderNumericField('marcaSegundos', 'Seg.', item.marcaSegundos, arrayName, index)}
            {renderNumericField('marcaCentesimas', 'Centes.', item.marcaCentesimas, arrayName, index)}
          </>
        );
      case 'medioFondo':
        return (
          <>
            {renderNumericField('marcaMinutos', 'Min.', item.marcaMinutos, arrayName, index)}
            {renderNumericField('marcaSegundos', 'Seg.', item.marcaSegundos, arrayName, index)}
            {renderNumericField('marcaCentesimas', 'Centes.', item.marcaCentesimas, arrayName, index)}
          </>
        );
      case 'fondo':
        return (
          <>
            {renderNumericField('marcaHoras', 'Hr', item.marcaHoras, arrayName, index)}
            {renderNumericField('marcaMinutos', 'Min', item.marcaMinutos, arrayName, index)}
            {renderNumericField('marcaSegundos', 'Seg', item.marcaSegundos, arrayName, index)}
            {renderNumericField('marcaCentesimas', 'Cent', item.marcaCentesimas, arrayName, index)}
          </>
        );
      case 'campo':
        return (
          <>
            <Grid item xs={6} sm={3}>
              <TextField
                label="Met."
                name={`${arrayName}[${index}].marcaMetros`}
                value={item.marcaMetros || ''}
                onChange={handleChange}
                fullWidth
                size="small"
                inputProps={{
                  maxLength: 2,
                  onInput: (e) => {
                    if (e.target.value.length >= 2) {
                      const inputs = Array.from(document.querySelectorAll('input'));
                      const currentIndex = inputs.indexOf(e.target);
                      if (currentIndex > -1 && currentIndex < inputs.length - 1) {
                        inputs[currentIndex + 1].focus();
                      }
                    }
                  },
                  onBlur: (e) => {
                    if (e.target.value && e.target.value.length === 1) {
                      setFieldValue(`${arrayName}[${index}].marcaMetros`, e.target.value.padStart(2, '0'));
                    }
                  },
                }}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                label="Cent."
                name={`${arrayName}[${index}].marcaCentimetros`}
                value={item.marcaCentimetros || ''}
                onChange={handleChange}
                fullWidth
                size="small"
                inputProps={{
                  maxLength: 2,
                  onInput: (e) => {
                    if (e.target.value.length >= 2) {
                      const inputs = Array.from(document.querySelectorAll('input'));
                      const currentIndex = inputs.indexOf(e.target);
                      if (currentIndex > -1 && currentIndex < inputs.length - 1) {
                        inputs[currentIndex + 1].focus();
                      }
                    }
                  },
                  onBlur: (e) => {
                    if (e.target.value && e.target.value.length === 1) {
                      setFieldValue(`${arrayName}[${index}].marcaCentimetros`, e.target.value.padStart(2, '0'));
                    }
                  },
                }}
              />
            </Grid>
          </>
        );
      case 'combinados':
        return (
          <Grid item xs={12} sm={4}>
            <TextField
              label="Puntos"
              name={`${arrayName}[${index}].marcaPuntos`}
              value={item.marcaPuntos || ''}
              onChange={handleChange}
              fullWidth
              size="small"
              inputProps={{
                maxLength: 4,
                onInput: (e) => {
                  if (e.target.value.length >= 4) {
                    const inputs = Array.from(document.querySelectorAll('input'));
                    const currentIndex = inputs.indexOf(e.target);
                    if (currentIndex > -1 && currentIndex < inputs.length - 1) {
                      inputs[currentIndex + 1].focus();
                    }
                  }
                },
                onBlur: (e) => {
                  if (e.target.value && e.target.value.length === 1) {
                    setFieldValue(`${arrayName}[${index}].marcaPuntos`, e.target.value.padStart(2, '0'));
                  }
                },
              }}
            />
          </Grid>
        );
      default:
        return null;
    }
  };

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteCallback, setDeleteCallback] = useState(() => () => {});
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const handleDelete = (callback) => {
    setDeleteCallback(() => callback);
    setConfirmOpen(true);
  };

  const handleConfirmDelete = () => {
    deleteCallback();
    setConfirmOpen(false);
    setSnackbarOpen(true);
  };

  const handleCancelDelete = () => setConfirmOpen(false);
  const handleCloseSnackbar = () => setSnackbarOpen(false);

  return (
    <>
      <Grid item xs={12}>
        <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
          Pruebas Individuales
        </Typography>
        <FieldArray
          name="pruebasData"
          render={(arrayHelpers) => (
            <>
              {values.pruebasData && values.pruebasData.length > 0 ? (
                values.pruebasData.map((item, index) => {
                  const options = getOptions(values.pruebasData, index, individualOptions);
                  const selectedTest = item.prueba ? options.find((p) => p.nombre === item.prueba) : null;
                  return (
                    <Grid container spacing={1} key={`individual-${index}`} sx={{ mb: 2 }} alignItems="center">
                      <Grid item xs={12} sm={4}>
                        <Autocomplete
                          freeSolo
                          options={options}
                          getOptionLabel={(option) => option.nombre}
                          value={selectedTest || null}
                          onChange={(e, newValue) => {
                            arrayHelpers.replace(index, {
                              ...item,
                              prueba: newValue ? newValue.nombre : '',
                              tipoMarca: newValue ? newValue.tipoMarca : '',
                              eventCode: newValue ? newValue.eventCode : '',
                              catNumber: newValue ? newValue.catNumber : '',
                            });
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label={`Prueba Individual #${index + 1}`}
                              name={`pruebasData[${index}].prueba`}
                              required
                              size="small"
                            />
                          )}
                        />
                      </Grid>
                      {renderMarcaFields(item, index, 'pruebasData')}
                      {values.pruebasData.length > 1 && (
                        <Grid item xs={12} sm={1}>
                          <IconButton color="error" onClick={() => handleDelete(() => arrayHelpers.remove(index))} size="small">
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Grid>
                      )}
                    </Grid>
                  );
                })
              ) : (
                <Grid container spacing={1} sx={{ mb: 1 }}>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label="Prueba Individual"
                      name="pruebasData[0].prueba"
                      value=""
                      onChange={(e) => {
                        setFieldValue('pruebasData', [{ prueba: e.target.value, tipoMarca: '', catNumber: '', eventCode: '' }]);
                      }}
                      required
                      fullWidth
                      size="small"
                    />
                  </Grid>
                </Grid>
              )}
              {values.pruebasData && values.pruebasData.length < maxPruebas && (
                <IconButton color="primary" onClick={() => arrayHelpers.push({ prueba: '', tipoMarca: '', eventcode: '', catNumber: '', eventCode: '' })} size="small">
                  <AddIcon fontSize="small" />
                  <Typography variant="caption" sx={{ ml: 0.5 }}>
                    Agregar Prueba Individual
                  </Typography>
                </IconButton>
              )}
            </>
          )}
        />
      </Grid>
      {/* FieldArray para Pruebas de Relevos */}
      <Grid item xs={12}>
        <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
          Relevos
        </Typography>
        <FieldArray
          name="relevosData"
          render={(arrayHelpers) => (
            <>
              {values.relevosData && values.relevosData.length > 0 ? (
                values.relevosData.map((item, index) => {
                  const options = getOptions(values.relevosData, index, relayOptions);
                  const selectedTest = item.prueba ? options.find((p) => p.nombre === item.prueba) : null;
                  return (
                    <Grid container spacing={1} key={`relevo-${index}`} sx={{ mb: 2 }} alignItems="center">
                      <Grid item xs={12} sm={4}>
                        <Autocomplete
                          freeSolo
                          options={options}
                          getOptionLabel={(option) => option.nombre}
                          value={selectedTest || null}
                          onChange={(e, newValue) => {
                            arrayHelpers.replace(index, {
                              ...item,
                              prueba: newValue ? newValue.nombre : '',
                              tipoMarca: newValue ? newValue.tipoMarca : '',
                              eventCode: newValue ? newValue.eventCode : '',
                              catNumber: newValue ? newValue.catNumber : '',
                            });
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label={`Prueba de Relevo #${index + 1}`}
                              name={`relevosData[${index}].prueba`}
                              size="small"
                            />
                          )}
                        />
                      </Grid>
                      {renderMarcaFields(item, index, 'relevosData')}
                      {values.relevosData.length > 1 && (
                        <Grid item xs={12} sm={1}>
                          <IconButton color="error" onClick={() => handleDelete(() => arrayHelpers.remove(index))} size="small">
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Grid>
                      )}
                    </Grid>
                  );
                })
              ) : (
                <Grid container spacing={1} sx={{ mb: 1 }}>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label="Prueba de Relevo"
                      name="relevosData[0].prueba"
                      value=""
                      onChange={(e) => {
                        setFieldValue('relevosData', [{ prueba: e.target.value, tipoMarca: '', eventcode: '', catNumber: '', eventCode: '' }]);
                      }}
                      fullWidth
                      size="small"
                    />
                  </Grid>
                </Grid>
              )}
              {values.relevosData && values.relevosData.length < maxRelevos && (
                <IconButton
                  color="primary"
                  onClick={() =>
                    arrayHelpers.push({
                      prueba: '',
                      tipoMarca: '',
                      integrantes: Array.from({ length: opcionesRelevos?.maxMiembros || 0 }, () => ''),
                      suplentes: opcionesRelevos?.permitirSuplentes
                        ? Array.from({ length: opcionesRelevos?.numeroSuplentes || 0 }, () => '')
                        : [],
                      equipoTipo: opcionesRelevos?.permitirEquiposMixtos ? 'Varonil' : '',
                    })
                  }
                  size="small"
                >
                  <AddIcon fontSize="small" />
                  <Typography variant="caption" sx={{ ml: 0.5 }}>
                    Agregar Prueba de Relevo
                  </Typography>
                </IconButton>
              )}
            </>
          )}
        />
      </Grid>
      {/* Diálogo de confirmación */}
      <Dialog open={confirmOpen} onClose={handleCancelDelete}>
        <DialogTitle>Confirmación</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Estás seguro de que deseas eliminar esta prueba?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete}>Cancelar</Button>
          <Button onClick={handleConfirmDelete} color="error">
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        message="Prueba eliminada"
      />
    </>
  );
};

PruebasFieldArray.propTypes = {
  values: PropTypes.object.isRequired,
  errors: PropTypes.object,
  touched: PropTypes.object,
  setFieldValue: PropTypes.func.isRequired,
  handleChange: PropTypes.func.isRequired,
  pruebasFiltradas: PropTypes.array.isRequired,
  maxPruebas: PropTypes.number.isRequired,
  maxRelevos: PropTypes.number.isRequired,
  habilitarMarcasSiembra: PropTypes.bool.isRequired,
  opcionesRelevos: PropTypes.shape({
    maxMiembros: PropTypes.number,
    numeroSuplentes: PropTypes.number,
    permitirSuplentes: PropTypes.bool,
    maxRelevosPorRegistro: PropTypes.number,
    permitirEquiposMixtos: PropTypes.bool,
  }).isRequired,
};

export default PruebasFieldArray;
